import React from 'react';
import { createPortal } from 'react-dom';
import {
  History,
  X,
  CheckCircle2,
  XCircle,
  ArrowUpRight,
  RotateCcw,
  Clock,
} from 'lucide-react';
import { ROLE_LABELS } from '../../lib/constants';

const ACTION_CONFIG = {
  APPROVED: {
    Icon: CheckCircle2,
    badge: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    dot: 'bg-emerald-500',
    label: 'Approved',
  },
  REJECTED: {
    Icon: XCircle,
    badge: 'text-rose-700 bg-rose-50 border-rose-200',
    dot: 'bg-rose-500',
    label: 'Rejected',
  },
  RESUBMITTED: {
    Icon: RotateCcw,
    badge: 'text-blue-700 bg-blue-50 border-blue-200',
    dot: 'bg-blue-500',
    label: 'Resubmitted',
  },
  SUBMITTED: {
    Icon: ArrowUpRight,
    badge: 'text-amber-700 bg-amber-50 border-amber-200',
    dot: 'bg-amber-500',
    label: 'Submitted for Review',
  },
};

const ENTITY_CONFIG = {
  PROFILE: { label: 'Profile', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  PREQUALIFICATION: { label: 'Prequalification', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  TENDER: { label: 'Tender / Proposal', badge: 'bg-purple-50 text-purple-700 border-purple-200' },
};

export default function ApprovalHistoryModal({ request, onClose }) {
  if (!request) return null;

  const history = request.history || [];
  const entityType = request.entityType || 'PROFILE';
  const entityConfig = ENTITY_CONFIG[entityType] || { label: entityType, badge: 'bg-gray-100 text-gray-600 border-gray-200' };

  const modalContent = (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col animate-scale-in border border-gray-100 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 border border-blue-100">
              <History className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${entityConfig.badge}`}>
                  {entityConfig.label}
                </span>
                <h3 className="text-base font-bold text-gray-900">Approval History</h3>
              </div>
              <p className="text-xs text-gray-400 mt-0.5 truncate max-w-sm">
                {request.title || 'Approval Request'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Timeline Content */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {history.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-8 h-8 text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-500 font-medium">No audit logs yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Activity will appear here as this request progresses through the approval workflow.
              </p>
            </div>
          ) : (
            <div className="relative border-l-2 border-gray-100 ml-3 space-y-5">
              {history.map((item, idx) => {
                const config = ACTION_CONFIG[item.action] || ACTION_CONFIG.SUBMITTED;
                const IconComponent = config.Icon;

                return (
                  <div key={item.id || idx} className="relative pl-6">
                    {/* Timeline dot */}
                    <span
                      className={`absolute -left-[9px] top-2 w-4 h-4 rounded-full border-2 border-white shadow ${config.dot}`}
                    />

                    {/* Card */}
                    <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3.5 space-y-2.5">
                      {/* Action row */}
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${config.badge}`}
                          >
                            <IconComponent className="w-3 h-3" />
                            {config.label}
                          </span>
                          <div>
                            <span className="text-xs font-semibold text-gray-800">
                              {item.actor?.username || 'System'}
                            </span>
                            {item.actor?.role && (
                              <span className="ml-1.5 text-[10px] text-gray-400">
                                ({ROLE_LABELS[item.actor.role] || item.actor.role})
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="text-[11px] text-gray-400 flex items-center gap-1 whitespace-nowrap shrink-0">
                          <Clock className="w-3 h-3" />
                          {item.createdAt ? new Date(item.createdAt).toLocaleString() : ''}
                        </span>
                      </div>

                      {/* Remarks / Notes */}
                      {item.remarks && (
                        <div className="text-xs text-gray-700 bg-white p-2.5 rounded-lg border border-slate-100">
                          <span className="font-semibold text-gray-400 block text-[10px] uppercase tracking-wider mb-1">
                            Remarks / Notes
                          </span>
                          <p className="whitespace-pre-wrap leading-relaxed">{item.remarks}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
